/**
 * Landing FX — plain JS on purpose (no build step surprises). Everything is
 * null-guarded so template edits can't throw, and every listener/timer/
 * observer is registered through the hooks that renderLanding tears down.
 *
 * Performance contract (the page used to drop frames on every scroll):
 *  - ONE rAF loop. Not one per effect.
 *  - ONE scroll listener and ONE debounced resize listener, fanned out to
 *    hooks. Frost plates are expensive to paint; they must not repaint on
 *    every resize event of a window drag.
 *  - The frost sheets (hero + one per warband monolith) composite only while
 *    a pointer is warming them or their melt mask still holds heat. Once a
 *    sheet has refrozen it stops touching the canvas entirely. Previously all
 *    five recomposited every frame forever — ~15 full-canvas blits per frame,
 *    which was the bulk of the jank.
 *  - Anything tied to the hero (parallax, snow, aurora, ice sheet, raven) is
 *    gated on the hero actually being on screen.
 *  - Style writes are diffed. Setting the same value every frame still costs
 *    a recalc, and .hero-aurora is a full-viewport mix-blend-mode:screen
 *    layer — re-compositing it 60×/s for a value that didn't change is pure
 *    waste.
 */
export function initLanding(root, onEnter, isDead, setSysTimer, onWin, regIO) {
  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = matchMedia('(pointer:fine)').matches;
  var raf = requestAnimationFrame;

  // Play buttons → SPA login (no full reload). When the ice-break FX is armed
  // (set up below), the burst plays first and entry follows a beat later.
  var enterFx = null;
  root.querySelectorAll('[data-enter]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (enterFx && enterFx(a)) {
        setTimeout(function () { if (!isDead()) onEnter(); }, 340);
      } else onEnter();
    });
  });

  // burger / mobile nav
  var burger = root.querySelector('.burger');
  var mnav = root.querySelector('#mnav');
  if (burger && mnav) {
    burger.addEventListener('click', function () {
      var open = mnav.classList.toggle('open');
      burger.setAttribute('aria-expanded', String(open));
    });
    mnav.addEventListener('click', function () { mnav.classList.remove('open'); });
  }

  // FAQ accordion — the open state is a class, and the height comes from a
  // 0fr→1fr grid row. No scrollHeight read (that forced a sync layout on
  // every click) and no max-height animation (that thrashes layout each frame).
  root.querySelectorAll('.fq').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var item = btn.parentElement;
      var wasOpen = btn.getAttribute('aria-expanded') === 'true';
      root.querySelectorAll('.faq.open').forEach(function (o) {
        o.classList.remove('open');
        var b = o.querySelector('.fq');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
      if (!wasOpen && item) {
        item.classList.add('open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* ── the two shared listeners ──────────────────────────────────────────
     Every effect reads scrollY / lastMove from here rather than attaching
     its own handler, and repaint-heavy work subscribes to resizeHooks. */
  var scrollY = root.scrollTop;
  var lastMove = performance.now();
  root.addEventListener('scroll', function () {
    scrollY = root.scrollTop;
    lastMove = performance.now();
  }, { passive: true });
  onWin('mousemove', function () { lastMove = performance.now(); });

  var resizeHooks = [];
  var resizeT = 0;
  onWin('resize', function () {
    clearTimeout(resizeT);
    resizeT = setTimeout(function () {
      if (isDead()) return;
      for (var i = 0; i < resizeHooks.length; i++) resizeHooks[i]();
    }, 150);
  });

  var mkIO = function (cb, margin) {
    if (typeof IntersectionObserver === 'undefined') return null;
    return regIO(new IntersectionObserver(cb, { root: root, rootMargin: margin || '0px' }));
  };

  // LIVE BACKGROUNDS — generated ambient loops (hold, finale, rune stone).
  // Lazy: a video gets its src only when it nears the viewport, pauses when it
  // leaves, and under prefers-reduced-motion never loads at all — the painted
  // posters underneath stand in.
  var lives = root.querySelectorAll('video[data-live]');
  if (lives.length && !reduce) {
    var lio = mkIO(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting) {
          if (!v.getAttribute('src')) v.setAttribute('src', v.getAttribute('data-live'));
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (v.getAttribute('src')) {
          v.pause();
        }
      });
    }, '240px');
    if (lio) lives.forEach(function (v) { lio.observe(v); });
  }

  // scroll-spy for the lintel links + the rune that catches the aurora
  var links = root.querySelectorAll('.lintel-nav a[href^="#"]');
  if (links.length) {
    var byId = {};
    links.forEach(function (a) { byId[a.getAttribute('href').slice(1)] = a; });
    var io = mkIO(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        links.forEach(function (a) { a.classList.remove('on'); });
        var a = byId[en.target.id];
        if (a) a.classList.add('on');
        var rune = en.target.querySelector('.shead .runic');
        if (rune) rune.classList.add('lit');
      });
    }, '-40% 0px -55% 0px');
    if (io) ['top', 'crew', 'how', 'city', 'rise', 'guides'].forEach(function (id) {
      var el = root.querySelector('#' + id);
      if (el) io.observe(el);
    });
  }

  // The CSS aurora drift is an infinite keyframe. Park it while its element
  // is off screen. (The ice titles used to be gated here too — their glint
  // sweep is gone entirely now, they're static paint.)
  var gated = root.querySelectorAll('.hero-aurora');
  if (gated.length) {
    var gio = mkIO(function (entries) {
      entries.forEach(function (en) { en.target.classList.toggle('offvp', !en.isIntersecting); });
    }, '10% 0px');
    if (gio) gated.forEach(function (el) { el.classList.add('offvp'); gio.observe(el); });
  }

  if (reduce) return;

  /* ═══ MOTION ══════════════════════════════════════════════════════════ */

  var hero = root.querySelector('.hero');
  var heroVis = true;
  if (hero) {
    var hio = mkIO(function (e) { heroVis = e[0].isIntersecting; }, '80px');
    if (hio) hio.observe(hero);
  }

  // hero parallax — the painting drifts slower than the page
  var heroBg = root.querySelector('#heroBg');
  var parY = -1;

  // (The hero snow/embers canvas is gone — QA 86cb1qvpt: the drifting-snow
  // layer read as a rendering bug over the painted art, twice. The painting
  // carries its own snow; the ambience lives in the frost sheets now.)

  /* ═══ SIGNATURE FX — the setting's own tricks ═══
     1. frost breath: the cursor exhales cold — mist + crystallising flecks
     2. raven flyby: a shadow crosses the page every half-minute or so
     3. ice-break on Play: the click shatters a sheet of ice, then you enter
     4. rune ignite (wired into the scroll-spy IO above) */
  var fxc = document.createElement('canvas');
  fxc.className = 'fxcanvas';
  root.appendChild(fxc); // inside root → renderLanding's teardown removes it
  var fctx = fxc.getContext('2d');
  var FW = 0, FH = 0;
  var fresize = function () { FW = fxc.width = root.clientWidth; FH = fxc.height = window.innerHeight; };
  fresize();
  resizeHooks.push(fresize);
  var parts = [];
  var fxPainted = false; // did the last frame leave anything on the fx canvas?

  // 1) frost breath — desktop pointers only
  if (fine) {
    var lmx = 0, lmy = 0, lemit = 0;
    root.addEventListener('mousemove', function (e) {
      var t = performance.now();
      var dx = e.clientX - lmx, dy = e.clientY - lmy;
      var sp = Math.hypot(dx, dy);
      lmx = e.clientX; lmy = e.clientY;
      if (t - lemit < 30 || sp < 3 || parts.length > 260) return;
      lemit = t;
      for (var k = 0; k < 2; k++) {
        parts.push({ type: 'mist', x: e.clientX + (Math.random() - 0.5) * 8, y: e.clientY + (Math.random() - 0.5) * 8,
          vx: dx * 2 + (Math.random() - 0.5) * 14, vy: dy * 2 - 10 - Math.random() * 14,
          r: 3 + Math.random() * 7, life: 0, max: 0.9 + Math.random() * 0.5 });
      }
      if (Math.random() < 0.3) {
        parts.push({ type: 'crystal', x: e.clientX, y: e.clientY,
          vx: (Math.random() - 0.5) * 20, vy: 10 + Math.random() * 22, a: Math.random() * Math.PI,
          r: 1.6 + Math.random() * 2.6, life: 0, max: 1.1 + Math.random() * 0.6 });
      }
    }, { passive: true });
  }

  // 3) ice-break — arm the Play handler set up at the top
  enterFx = function (a) {
    var r = a.getBoundingClientRect(), cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    for (var k = 0; k < 26; k++) {
      var ang = Math.random() * Math.PI * 2, v = 130 + Math.random() * 280;
      parts.push({ type: 'shard', x: cx, y: cy, vx: Math.cos(ang) * v, vy: Math.sin(ang) * v - 70,
        a: ang, spin: (Math.random() - 0.5) * 9, len: 5 + Math.random() * 12, life: 0, max: 0.55 + Math.random() * 0.3 });
    }
    parts.push({ type: 'ring', x: cx, y: cy, life: 0, max: 0.45 });
    return true;
  };

  // 2) the raven
  var raven = document.createElement('div');
  raven.className = 'lraven';
  raven.innerHTML = '<svg viewBox="0 0 64 40" aria-hidden="true">'
    + '<path class="wingL" d="M32 18 C22 8 10 4 1 7 C10 12 16 15 22 20 Z"/>'
    + '<path class="wingR" d="M32 18 C42 8 54 4 63 7 C54 12 48 15 42 20 Z"/>'
    + '<path d="M30 16 C34 14 38 15 41 18 C45 19 48 21 50 24 L44 23 C40 28 34 30 28 27 C24 30 20 30 16 28 C20 26 23 24 25 21 Z"/>'
    + '</svg>';
  root.appendChild(raven);
  var ravenT = -1, ravenDur = 8000, ravenY0 = 0, ravenY1 = 0, ravenStart = 0;

  // schedulers
  var nextRaven = performance.now() + 8000 + Math.random() * 9000;
  // 6) the aurora leans toward the cursor
  var auroraEl = root.querySelector('.hero-aurora');
  var aurTarget = 0, aurNow = 0, aurWrote = -999;
  if (auroraEl && fine) {
    auroraEl.style.animation = 'none'; // the cursor drives it now, not the loop
    root.addEventListener('mousemove', function (e) {
      aurTarget = (e.clientX / Math.max(1, FW)) * 100;
    }, { passive: true });
  } else {
    auroraEl = null; // touch devices keep the CSS drift
  }

  // 7) the cold takes you — frost creeps in from the corners while you idle
  var frost = document.createElement('div');
  frost.className = 'lfrost';
  root.appendChild(frost);
  var frostOp = -1;

  // 8) wolf eyes blink in the dark
  var eyes = document.createElement('div');
  eyes.className = 'leyes';
  eyes.innerHTML = '<i></i><i></i>';
  root.appendChild(eyes);
  var nextEyes = performance.now() + 12000 + Math.random() * 10000;

  // 10) lens flakes — a big flake sticks to the glass and melts
  var nextLens = performance.now() + 9000 + Math.random() * 9000;

  /* ═══ FROST SHEETS ═══════════════════════════════════════════════════
     The hero art and every warband monolith sit behind real frost. The
     cursor is the one warm thing in the scene and melts it; the cold always
     grows back. Each sheet keeps a static frost plate, a melt mask, and the
     visible canvas = plate minus mask.

     The mask only changes while a pointer is warming it or while the last
     warm spot is still decaying, so `dirty` is false most of the time and
     the whole sheet costs nothing. `idle` counts frames since the pointer
     left; past `refreeze` the residual mask alpha is below the visible
     threshold, so we clear it, draw the clean plate once, and go quiet. */
  var sheets = [];

  var blit = function (s) {
    s.ctx.globalCompositeOperation = 'copy';
    s.ctx.drawImage(s.plate, 0, 0);
    s.ctx.globalCompositeOperation = 'destination-out';
    s.ctx.drawImage(s.mask, 0, 0);
  };

  var mkSheet = function (cfg) {
    var host = cfg.host;
    var c = document.createElement('canvas');
    c.className = cfg.cls;
    host.insertBefore(c, cfg.before || null);
    var s = {
      host: host, c: c, ctx: c.getContext('2d'),
      plate: document.createElement('canvas'),
      mask: document.createElement('canvas'),
      w: 0, h: 0, x: -999, y: -999,
      on: false, idle: 1e9, dirty: true, vis: true, cfg: cfg,
    };
    s.pctx = s.plate.getContext('2d');
    s.mctx = s.mask.getContext('2d');

    var size = function () {
      var r = host.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
      if (w === s.w && h === s.h) return; // repainting a plate is expensive; skip no-op resizes
      s.w = c.width = s.plate.width = s.mask.width = w;
      s.h = c.height = s.plate.height = s.mask.height = h;
      cfg.paint(s.pctx, w, h);
      s.mctx.clearRect(0, 0, w, h);
      s.idle = 1e9; s.dirty = true;
    };
    size();
    resizeHooks.push(size);

    host.addEventListener('pointermove', function (e) {
      var r = host.getBoundingClientRect();
      s.x = e.clientX - r.left; s.y = e.clientY - r.top;
      s.on = true; s.idle = 0; s.dirty = true;
    }, { passive: true });
    host.addEventListener('pointerleave', function () { s.on = false; }, { passive: true });

    sheets.push(s);
  };

  var stepSheet = function (s) {
    if (!s.vis) {
      // scrolled away — the cold reclaims it while you aren't looking
      if (s.dirty) {
        s.mctx.clearRect(0, 0, s.w, s.h);
        s.on = false; s.idle = 1e9;
        blit(s);
        s.dirty = false;
      }
      return;
    }
    if (!s.dirty) return;
    var cfg = s.cfg;
    if (s.on) {
      var g = s.mctx.createRadialGradient(s.x, s.y, cfg.inner, s.x, s.y, cfg.radius);
      g.addColorStop(0, 'rgba(255,255,255,' + cfg.warmth + ')');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      s.mctx.globalCompositeOperation = 'source-over';
      s.mctx.fillStyle = g;
      s.mctx.beginPath(); s.mctx.arc(s.x, s.y, cfg.radius, 0, Math.PI * 2); s.mctx.fill();
      // meltwater runs: thin streaks below the warm spot
      if (Math.random() < cfg.runChance) {
        s.mctx.fillStyle = cfg.runInk;
        s.mctx.fillRect(s.x + (Math.random() - 0.5) * cfg.runSpread, s.y, cfg.runW, cfg.runMin + Math.random() * cfg.runVar);
      }
    } else {
      s.idle++;
    }
    // the cold grows back — the mask decays a touch every frame
    s.mctx.globalCompositeOperation = 'destination-out';
    s.mctx.fillStyle = 'rgba(0,0,0,' + (s.on ? cfg.decayOn : cfg.decayOff) + ')';
    s.mctx.fillRect(0, 0, s.w, s.h);
    blit(s);
    if (!s.on && s.idle > cfg.refreeze) {
      s.mctx.clearRect(0, 0, s.w, s.h);
      blit(s);
      s.dirty = false; // fully refrozen — stop compositing until warmed again
    }
  };

  // fern crystals growing in from the edges, then sparkle flecks
  var ferns = function (f, w, h, count, ink, minLen, varLen) {
    for (var i = 0; i < count; i++) {
      var edge = Math.floor(Math.random() * 4);
      var x = edge === 0 ? 0 : edge === 1 ? w : Math.random() * w;
      var y = edge < 2 ? Math.random() * h : (edge === 2 ? 0 : h);
      var ang = Math.atan2(h / 2 - y, w / 2 - x) + (Math.random() - 0.5) * 1.2;
      var seg = (minLen + Math.random() * varLen) / 3;
      f.strokeStyle = 'rgba(' + ink + ',' + (0.10 + Math.random() * 0.16) + ')';
      f.lineWidth = 0.8 + Math.random();
      f.beginPath(); f.moveTo(x, y);
      var px = x, py = y;
      for (var s = 0; s < 3; s++) {
        px += Math.cos(ang) * seg; py += Math.sin(ang) * seg;
        ang += (Math.random() - 0.5) * 0.9;
        f.lineTo(px, py);
      }
      f.stroke();
    }
  };
  var flecks = function (f, w, h, count) {
    for (var j = 0; j < count; j++) {
      f.fillStyle = 'rgba(255,255,255,' + (0.05 + Math.random() * 0.2) + ')';
      f.fillRect(Math.random() * w, Math.random() * h, 1.2, 1.2);
    }
  };

  // Melt sheets are a CURSOR mechanic — on touch there is nothing to melt
  // them with, and their canvases (one per monolith, x3 DPR) are exactly the
  // layers iOS Safari stalls on mid-flick (the black-viewport hitch a screen
  // recording caught on a real iPhone). Pointerless devices skip them and
  // keep the CSS sheen (.wm-ice / .wm-cracks) as the frost.
  if (hero && fine) {
    mkSheet({
      host: hero, cls: 'licesheet', before: hero.querySelector('.hero-in'),
      inner: 8, radius: 85, warmth: 0.5,
      runChance: 0.3, runInk: 'rgba(255,255,255,0.22)', runSpread: 60, runW: 1.6, runMin: 24, runVar: 60,
      decayOn: 0.011, decayOff: 0.011, refreeze: 460,
      paint: function (f, w, h) {
        f.clearRect(0, 0, w, h);
        // haze — thinner in the middle so the painting ghosts through.
        // QA (86cb1qvpt): at full strength this fogged the whole hero white on
        // any pointerless view (touch, first paint, screenshots) — the plate
        // must READ as frost on glass, never hide the painting behind it.
        var g = f.createRadialGradient(w / 2, h * 0.45, Math.min(w, h) * 0.18, w / 2, h / 2, Math.max(w, h) * 0.72);
        g.addColorStop(0, 'rgba(214,234,250,0.10)');
        g.addColorStop(1, 'rgba(222,238,252,0.30)');
        f.fillStyle = g; f.fillRect(0, 0, w, h);
        ferns(f, w, h, 170, '240,250,255', 30, 120);
        flecks(f, w, h, 220);
      },
    });
  }

  if (fine) root.querySelectorAll('.wmono').forEach(function (mono) {
    mkSheet({
      host: mono, cls: 'wm-melt', before: mono.querySelector('.wm-base'),
      inner: 6, radius: 62, warmth: 0.55,
      runChance: 0.25, runInk: 'rgba(255,255,255,0.25)', runSpread: 40, runW: 1.4, runMin: 18, runVar: 40,
      decayOn: 0.008, decayOff: 0.03, refreeze: 175,
      paint: function (f, w, h) {
        f.clearRect(0, 0, w, h);
        // same QA rule as the hero sheet: frost hints at the figure, it does
        // not white the monolith out for pointerless viewers
        var g = f.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, 'rgba(222,240,252,0.32)');
        g.addColorStop(0.5, 'rgba(198,226,246,0.24)');
        g.addColorStop(1, 'rgba(214,236,252,0.30)');
        f.fillStyle = g; f.fillRect(0, 0, w, h);
        ferns(f, w, h, 70, '244,252,255', 26, 60);
        flecks(f, w, h, 90);
      },
    });
  });

  if (sheets.length) {
    var sio = mkIO(function (entries) {
      entries.forEach(function (en) {
        for (var i = 0; i < sheets.length; i++) {
          if (sheets[i].host === en.target) { sheets[i].vis = en.isIntersecting; break; }
        }
      });
    }, '120px');
    if (sio) sheets.forEach(function (s) { sio.observe(s.host); });
  }

  /* ═══ THE LOOP ════════════════════════════════════════════════════════ */
  var lastF = 0, lastE = 0;
  var loop = function (t) {
    if (isDead()) return;
    raf(loop);
    var dt = Math.min(0.05, (t - lastF) / 1000) || 0.016;
    lastF = t;

    // hero parallax — the painting drifts slower than the page
    if (heroBg && heroVis && scrollY < 1200) {
      var py = scrollY * 0.22;
      if (py !== parY) { parY = py; heroBg.style.transform = 'translateY(' + py + 'px)'; }
    }

    for (var si = 0; si < sheets.length; si++) stepSheet(sheets[si]);

    // aurora lean (lerped so it drifts, never snaps)
    if (auroraEl && heroVis) {
      aurNow += (aurTarget - aurNow) * Math.min(1, dt * 1.4);
      if (Math.abs(aurNow - aurWrote) > 0.05) {
        aurWrote = aurNow;
        auroraEl.style.backgroundPosition = aurNow.toFixed(2) + '% 0';
      }
    }

    // frost breathes in after ~6s of stillness, melts back on movement.
    // Capped well below 1 — at full opacity the corner gradients stacked into
    // a page-wide white-out on idle (QA 86cb1qvpt).
    var op = 0.45 * Math.max(0, Math.min(1, ((t - lastMove) / 1000 - 6) / 9));
    if (Math.abs(op - frostOp) > 0.004) {
      frostOp = op;
      frost.style.opacity = op.toFixed(3);
      frost.style.visibility = op > 0 ? 'visible' : 'hidden';
    }

    if (t > nextEyes) {
      nextEyes = t + 22000 + Math.random() * 20000;
      eyes.style.left = (6 + Math.random() * 80) + '%';
      eyes.style.top = (14 + Math.random() * 55) + '%';
      eyes.classList.remove('blink');
      void eyes.offsetWidth; // restart the animation
      eyes.classList.add('blink');
    }
    if (t > nextLens && parts.length < 240) {
      nextLens = t + 13000 + Math.random() * 11000;
      parts.push({ type: 'lens', x: FW * (0.1 + Math.random() * 0.8), y: FH * (0.1 + Math.random() * 0.6),
        vx: 0, vy: 2 + Math.random() * 3, r: 15 + Math.random() * 13, a: Math.random() * Math.PI,
        life: 0, max: 4.5 + Math.random() * 2 });
    }
    if (t > nextRaven && ravenT < 0) {
      // the raven belongs to the SKY — it only crosses while the hero's
      // horizon is on screen; over dark sections a black bird reads wrong
      var hr = hero ? hero.getBoundingClientRect() : null;
      if (hr && hr.bottom > 220 && hr.top < FH * 0.5) {
        ravenT = 0; ravenStart = t;
        var skyTop = Math.max(80, hr.top + 60);
        var skyBot = Math.min(FH * 0.6, hr.top + hr.height * 0.42);
        ravenY0 = skyTop + Math.random() * Math.max(40, skyBot - skyTop);
        ravenY1 = skyTop + Math.random() * Math.max(40, skyBot - skyTop);
        raven.style.opacity = '0.9';
        nextRaven = t + 26000 + Math.random() * 22000;
      } else {
        nextRaven = t + 6000; // try again once the sky is back
      }
    }
    if (ravenT >= 0) {
      var p = (t - ravenStart) / ravenDur;
      if (p >= 1) { ravenT = -1; raven.style.opacity = '0'; }
      else {
        var rx = -90 + p * (FW + 180);
        var ry = ravenY0 + (ravenY1 - ravenY0) * p + Math.sin(p * Math.PI * 3) * 24;
        raven.style.transform = 'translate(' + rx + 'px,' + ry + 'px) rotate(' + ((ravenY1 - ravenY0) * 0.02) + 'deg)';
      }
    }

    // particles — skip the full-viewport clear entirely once the last one dies
    if (!parts.length && !fxPainted) return;
    fctx.clearRect(0, 0, FW, FH);
    fxPainted = parts.length > 0;
    for (var i = parts.length - 1; i >= 0; i--) {
      var f = parts[i];
      f.life += dt;
      var p2 = f.life / f.max;
      if (p2 >= 1) { parts.splice(i, 1); continue; }
      if (f.type === 'mist') {
        f.x += f.vx * dt; f.y += f.vy * dt;
        fctx.beginPath();
        fctx.arc(f.x, f.y, f.r * (1 + p2 * 1.7), 0, Math.PI * 2);
        fctx.fillStyle = 'rgba(200,226,246,' + (0.14 * (1 - p2)) + ')';
        fctx.fill();
      } else if (f.type === 'crystal') {
        f.x += f.vx * dt; f.y += f.vy * dt; f.a += dt * 1.5;
        fctx.save(); fctx.translate(f.x, f.y); fctx.rotate(f.a);
        fctx.strokeStyle = 'rgba(214,238,255,' + (0.7 * (1 - p2)) + ')';
        fctx.lineWidth = 1;
        for (var s = 0; s < 3; s++) {
          fctx.rotate(Math.PI / 3);
          fctx.beginPath(); fctx.moveTo(-f.r, 0); fctx.lineTo(f.r, 0); fctx.stroke();
        }
        fctx.restore();
      } else if (f.type === 'shard') {
        f.vy += 900 * dt; f.x += f.vx * dt; f.y += f.vy * dt; f.a += f.spin * dt;
        fctx.save(); fctx.translate(f.x, f.y); fctx.rotate(f.a);
        fctx.fillStyle = 'rgba(190,224,250,' + (0.9 * (1 - p2)) + ')';
        fctx.beginPath(); fctx.moveTo(-f.len / 2, 0); fctx.lineTo(f.len / 2, -1.6); fctx.lineTo(f.len / 2, 1.6);
        fctx.closePath(); fctx.fill();
        fctx.restore();
      } else if (f.type === 'ring') {
        fctx.beginPath();
        fctx.arc(f.x, f.y, 8 + p2 * 130, 0, Math.PI * 2);
        fctx.strokeStyle = 'rgba(207,232,255,' + (0.8 * (1 - p2)) + ')';
        fctx.lineWidth = 2.5 * (1 - p2) + 0.5;
        fctx.stroke();
      } else if (f.type === 'lens') {
        // stuck to the glass: barely slides, blooms, then melts away
        f.y += f.vy * dt * (1 - p2);
        var la = p2 < 0.15 ? p2 / 0.15 : (1 - p2) / 0.85;
        var lr = f.r * (1 + p2 * 0.25);
        var lg = fctx.createRadialGradient(f.x, f.y, lr * 0.2, f.x, f.y, lr);
        lg.addColorStop(0, 'rgba(226,242,255,' + (0.16 * la) + ')');
        lg.addColorStop(0.7, 'rgba(210,234,252,' + (0.09 * la) + ')');
        lg.addColorStop(1, 'rgba(210,234,252,0)');
        fctx.fillStyle = lg;
        fctx.beginPath(); fctx.arc(f.x, f.y, lr, 0, Math.PI * 2); fctx.fill();
        fctx.save(); fctx.translate(f.x, f.y); fctx.rotate(f.a);
        fctx.strokeStyle = 'rgba(226,242,255,' + (0.28 * la) + ')';
        fctx.lineWidth = 1.2;
        for (var s2 = 0; s2 < 3; s2++) {
          fctx.rotate(Math.PI / 3);
          fctx.beginPath(); fctx.moveTo(-lr * 0.75, 0); fctx.lineTo(lr * 0.75, 0); fctx.stroke();
        }
        fctx.restore();
      }
    }
  };
  raf(loop);
}
